import {
  Users2Icon,
  PackageIcon,
  CalendarIcon,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  FilesIcon,
  NotebookPenIcon,
  ListChecksIcon,
  ListTodoIcon,
  CalendarClockIcon,
  BanknoteIcon,
  type LucideIcon,
  HomeIcon,
  CogIcon,
  ClipboardListIcon,
  TargetIcon,
  FlagIcon,
  ClipboardPenIcon,
  MessagesSquareIcon,
  ChevronDownIcon,
  BriefcaseBusinessIcon,
  FileSignatureIcon,
  ChartColumnIcon,
  GraduationCapIcon,
  MessageSquareIcon,
} from 'lucide-react';
import { cn } from '@gokrew/ui/lib/utils';
import styles from './Sidebar.module.css';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useCompanySwitch } from '@/hooks/useCompanySwitch';
import { type PermissionScope } from '@/types/permissions';
import { permissionKeys } from '@/utility';
import { PermissionGuard } from '../PermissionGuard';
import { useRouteWithPermission } from '@/hooks/useRouteWithPermission';
import { usePermissions } from '@/context/PermissionContext';
import { useGetAdminTasksSummary } from '@/hooks/single-tasks/useAdminTasks';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@gokrew/ui/hooks';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { getConfig } from '@gokrew/ui/runtime-config';
import { CompanySwitcher, CountBadge, useMessaging } from '@gokrew/ui';
import { KollabHRBadgeFullLogo, KollabHRBadgeLogo } from '@gokrew/ui/assets';
import { useAuth } from '@/context/AuthContext';
import { OnboardingFeatureKey } from '@gokrew/models/company';
import { useModuleEnabled } from '@/hooks/useModuleEnabled';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SidebarProps {
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

interface MenuItem {
  name: string;
  icon: LucideIcon;
  href: string;
  /** Other pages on which this entry is the current one (its tabs). */
  activePaths?: string[];
  tag?: string;
  badge?: number;
  hidden?: boolean;
  scope?: PermissionScope | PermissionScope[];
}

/** Folded until someone opens them, then remembered for that person. */
const DEFAULT_COLLAPSED_SECTIONS = new Set(['workspace', 'performance']);

interface NavSection {
  id: string;
  label: string;
  items: MenuItem[];
}

const SECTION_STATE_KEY = 'kollabhr.sidebar.sectionState.v2';

/**
 * Only the sections the user has actually clicked, as id -> open.
 *
 * Storing the whole closed list instead was wrong in two ways: a user who
 * opened every section stored an empty list, which is indistinguishable from
 * "no preference yet" and so overrode the defaults with silence; and a section
 * given a new default later could never reach anyone who had ever touched the
 * sidebar, because their snapshot already covered it. Recording overrides
 * alone leaves every untouched section on its default, for good.
 *
 * Reads are wrapped because storage throws in a private window and returns
 * nothing once site data is cleared, and neither is a reason not to render.
 */
type SectionState = Record<string, boolean>;

function readSectionState(): SectionState {
  try {
    const raw = window.localStorage.getItem(SECTION_STATE_KEY);
    if (raw) return JSON.parse(raw) as SectionState;
  } catch {
    // ignore: an untouched sidebar is the right fallback
  }
  return {};
}

function writeSectionState(state: SectionState): void {
  try {
    window.localStorage.setItem(SECTION_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore: the preference simply does not persist
  }
}

interface SidebarNavItemProps {
  item: MenuItem;
  isCollapsed: boolean;
}

const isUnder = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

function SidebarNavItem({ item, isCollapsed }: SidebarNavItemProps) {
  const { pathname } = useLocation();
  const onTab = !!item.activePaths?.some((path) => isUnder(pathname, path));
  const navLink = (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        cn(
          styles.navLink,
          (isActive || onTab) && styles.navLinkActive,
          isCollapsed && styles.navLinkCollapsed
        )
      }
    >
      <span className={styles.navIconWrap}>
        <item.icon size={16} className={styles.navIcon} />
      </span>

      {isCollapsed && !!item.badge && (
        <CountBadge count={item.badge} className={styles.messageBadge} />
      )}
      {!isCollapsed && (
        <div className={styles.navLabelWrapper}>
          <span className={styles.navLabel}>{item.name}</span>
          {!!item.badge && <CountBadge count={item.badge} />}
          {item.tag && <span className={styles.navTag}>{item.tag}</span>}
        </div>
      )}
    </NavLink>
  );

  return (
    <PermissionGuard scope={item.scope}>
      {isCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <li>{navLink}</li>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className={cn(
              styles.tooltipContent,
              'border-none bg-transparent text-inherit shadow-none'
            )}
            arrowClassName="!bg-[var(--brand-primary)] !fill-[var(--brand-primary)]"
          >
            {item.name}
          </TooltipContent>
        </Tooltip>
      ) : (
        <li>{navLink}</li>
      )}
    </PermissionGuard>
  );
}

function hasScopeAccess(
  scope: MenuItem['scope'],
  permissions: Set<Partial<PermissionScope>>
) {
  const scopes =
    scope === undefined ? [] : Array.isArray(scope) ? scope : [scope];
  if (scopes.length === 0) return true;
  return scopes.some((_scope) => permissions.has(_scope));
}

function isItemVisible(
  item: MenuItem,
  permissions: Set<Partial<PermissionScope>>
) {
  return !item.hidden && hasScopeAccess(item.scope, permissions);
}

function hasVisibleItems(
  items: MenuItem[],
  permissions: Set<Partial<PermissionScope>>
) {
  return items.some((item) => isItemVisible(item, permissions));
}

export function Sidebar({ isCollapsed, onCollapsedChange }: SidebarProps) {
  const { unreadCount: unreadMessages } = useMessaging();
  // Tasks assigned to this administrator (e.g. an onboarding step such as
  // "Create the employee in payroll"). Any admin can be assigned one,
  // whatever their role (D181), so the entry needs no scope; it shows once
  // they have ever had a task, with the open ones counted.
  const { data: myTasks } = useGetAdminTasksSummary();
  const myOpenTasks = myTasks
    ? myTasks.pending + myTasks.inProgress + myTasks.overdue
    : 0;
  const hasMyTasks = !!myTasks && myOpenTasks + myTasks.completed > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const { companies, companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.sidebar);
  const routeWithPermission = useRouteWithPermission();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { ENABLE_ASSET_V2_BETA, ENABLE_TRAINING, ENABLE_PAYROLL_EXPORT } =
    getConfig();
  const switchCompany = useCompanySwitch();
  const { permissions } = usePermissions();

  // A module switched off in Organisation; reads as on while flags load.
  const isModuleOn = useModuleEnabled();

  // Remembered per browser: a folded section is a display preference, not
  // something worth a profile round-trip.
  const [sectionState, setSectionState] =
    useState<SectionState>(readSectionState);

  const isSectionOpenById = useCallback(
    (id: string) => sectionState[id] ?? !DEFAULT_COLLAPSED_SECTIONS.has(id),
    [sectionState]
  );

  const toggleSection = useCallback((id: string) => {
    setSectionState((previous) => {
      const next = {
        ...previous,
        [id]: !(previous[id] ?? !DEFAULT_COLLAPSED_SECTIONS.has(id)),
      };
      writeSectionState(next);
      return next;
    });
  }, []);

  const isCollapsedRef = useRef(isCollapsed);
  const onCollapsedChangeRef = useRef(onCollapsedChange);
  isCollapsedRef.current = isCollapsed;
  onCollapsedChangeRef.current = onCollapsedChange;

  useEffect(() => {
    if (isMobile && !isCollapsedRef.current) {
      onCollapsedChangeRef.current(true);
    }
  }, [location.pathname, isMobile]);

  const sections: NavSection[] = [
    {
      id: 'overview',
      label: t('sections.overview'),
      items: [
        {
          name: t('nav.dashboard'),
          icon: HomeIcon,
          href: '/dashboard',
          scope: permissionKeys.dashboard.read,
        },
        {
          name: t('nav.analytics'),
          icon: ChartColumnIcon,
          href: '/analytics',
          scope: permissionKeys.analytics.read,
        },
      ],
    },
    {
      id: 'people',
      label: t('sections.people'),
      items: [
        {
          name: t('nav.employees'),
          icon: Users2Icon,
          href: '/employees',
          scope: permissionKeys.employee.read,
        },
        {
          name: t('nav.leave_management'),
          icon: CalendarIcon,
          href: '/leave-management',
          scope: [
            permissionKeys.leave_management.read,
            permissionKeys.leave_request.read,
          ],
        },
        {
          name: t('nav.recruiting'),
          icon: BriefcaseBusinessIcon,
          href: '/ats',
          hidden: !isModuleOn(OnboardingFeatureKey.Recruiting),
          scope: permissionKeys.recruiting.read,
        },
      ],
    },
    {
      id: 'operations',
      label: t('sections.operations'),
      items: [
        {
          name: t('nav.assets'),
          icon: PackageIcon,
          href: '/assets',
          // Two independent reasons to hide it: the beta flag, and the
          // company having switched the module off in Organisation.
          hidden:
            !ENABLE_ASSET_V2_BETA || !isModuleOn(OnboardingFeatureKey.Assets),
          scope: [permissionKeys.asset.read, permissionKeys.asset_model.read],
        },
        {
          name: t('nav.timesheet'),
          icon: CalendarClockIcon,
          href: '/timesheet',
          hidden: !isModuleOn(OnboardingFeatureKey.Timesheet),
          scope: permissionKeys.timesheet.read,
        },
        {
          // Its own scope, apart from Analytics: an operational file with
          // pay data (WP9). Not tied to the Timesheet module: leave, pay
          // changes and departures are exported without timesheets.
          name: t('nav.payroll_export'),
          icon: BanknoteIcon,
          href: '/payroll-export',
          // Behind the build flag until payroll v1 ships (PR 5).
          hidden: !ENABLE_PAYROLL_EXPORT,
          scope: permissionKeys.payroll_export.read,
        },
        {
          name: t('nav.tasks'),
          icon: ListChecksIcon,
          href: '/tasks',
          scope: permissionKeys.task_management.read,
        },
        {
          name: t('nav.my_tasks'),
          icon: ListTodoIcon,
          href: '/my-tasks',
          badge: myOpenTasks,
          hidden: !hasMyTasks,
        },
        {
          name: t('sidebar.surveys'),
          icon: ClipboardListIcon,
          href: '/survey',
          scope: permissionKeys.survey.read,
        },
        {
          name: t('nav.training'),
          icon: GraduationCapIcon,
          href: '/training',
          // Behind the build flag until the module ships (spec D119).
          hidden:
            !ENABLE_TRAINING || !isModuleOn(OnboardingFeatureKey.Training),
          scope: permissionKeys.training.read,
        },
      ],
    },
    {
      id: 'performance',
      label: t('sections.performance'),
      items: [
        {
          // One entry for reviews (automatic and one-time) and templates:
          // tabs on the page (ReviewsTabs), each keeping its own URL.
          name: t('nav.evaluations'),
          icon: TargetIcon,
          href: '/performance/reviews',
          activePaths: [
            '/performance/programs',
            '/performance/probation',
            '/performance/templates',
          ],
          // Beta until JP calls Performance fully tested (JP 2026-10-15). Never
          // drop it as a side effect of a feature PR: customers read it as "done".
          tag: t('nav.beta'),
          hidden: !isModuleOn(OnboardingFeatureKey.Performance),
          scope: permissionKeys.performance_review.read,
        },
        {
          name: t('nav.goals'),
          icon: FlagIcon,
          href: '/performance/goals',
          tag: t('nav.beta'),
          hidden: !isModuleOn(OnboardingFeatureKey.Performance),
          scope: permissionKeys.performance_review.read,
        },
        {
          name: t('nav.one_on_ones'),
          icon: MessagesSquareIcon,
          href: '/performance/one-on-ones',
          tag: t('nav.beta'),
          hidden: !isModuleOn(OnboardingFeatureKey.Performance),
        },
        {
          name: t('nav.my_reviews'),
          icon: ClipboardPenIcon,
          href: '/performance/my-reviews',
          tag: t('nav.beta'),
          hidden: !isModuleOn(OnboardingFeatureKey.Performance),
        },
      ],
    },
    {
      id: 'workspace',
      label: t('sections.workspace'),
      items: [
        {
          name: t('nav.messages'),
          icon: MessageSquareIcon,
          href: '/messages',
          badge: unreadMessages,
        },
        {
          name: t('nav.files'),
          icon: FilesIcon,
          href: '/files',
          scope: permissionKeys.file_management.read,
        },
        {
          name: t('nav.esignatures'),
          icon: FileSignatureIcon,
          href: '/esignatures',
          scope: permissionKeys.esignature.read,
        },
        {
          name: t('nav.notes'),
          icon: NotebookPenIcon,
          href: '/notes',
          scope: [
            permissionKeys.hr_notes.read,
            permissionKeys.employee_notes.read,
            permissionKeys.announcement.read,
          ],
        },
        // Administration held this one item, so the heading cost more rail
        // than it earned.
        {
          name: t('nav.organization'),
          icon: CogIcon,
          href: '/organization',
          scope: [
            permissionKeys.employee.read,
            permissionKeys.designation.read,
            permissionKeys.department.read,
            permissionKeys.asset_types.read,
            permissionKeys.asset_brand.read,
            permissionKeys.vendors.read,
            permissionKeys.company_role.read,
            permissionKeys.branches.read,
            permissionKeys.company.read,
            permissionKeys.access.read,
            permissionKeys.benefit.read,
          ],
        },
      ],
    },
  ];

  return (
    <>
      {!isCollapsed && (
        <div
          className={styles.overlay}
          onClick={() => onCollapsedChange(true)}
        />
      )}

      <div
        className={cn(
          styles.sidebar,
          isCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded
        )}
      >
        <div className={styles.header}>
          <div className={styles.headerInner}>
            <CompanySwitcher
              companies={companies || []}
              selectedCompanyId={companyId!}
              onCompanyChange={switchCompany}
              isCollapsed={isCollapsed}
              className={styles.companySection}
            />
            <button
              onClick={() => onCollapsedChange(!isCollapsed)}
              className={styles.toggleBtn}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <PanelRightCloseIcon size={16} />
              ) : (
                <PanelRightOpenIcon size={16} />
              )}
            </button>
          </div>
        </div>

        <nav className={styles.nav}>
          {sections.map((section, sectionIndex) => {
            if (!hasVisibleItems(section.items, permissions)) return null;

            // Section folding only exists while the rail shows labels; with
            // the whole sidebar collapsed there is no heading to click, and
            // hiding icons behind an invisible toggle would strand them.
            const isSectionOpen = isCollapsed || isSectionOpenById(section.id);

            return (
              <div
                key={section.id}
                className={cn(
                  styles.navSection,
                  sectionIndex > 0 && styles.navSectionSpaced
                )}
                style={
                  {
                    '--section-accent': `var(--sidebar-accent-${section.id}, var(--brand-primary))`,
                  } as CSSProperties
                }
              >
                {!isCollapsed && (
                  <button
                    type="button"
                    className={styles.sectionHeader}
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={isSectionOpen}
                  >
                    <span className={styles.sectionLabel}>{section.label}</span>
                    <ChevronDownIcon
                      size={14}
                      className={cn(
                        styles.sectionChevron,
                        !isSectionOpen && styles.sectionChevronClosed
                      )}
                    />
                  </button>
                )}

                <ul
                  className={cn(
                    styles.navList,
                    !isSectionOpen && styles.navListHidden
                  )}
                >
                  {section.items.map((item) =>
                    item.hidden ? null : (
                      <SidebarNavItem
                        key={item.href}
                        item={item}
                        isCollapsed={isCollapsed}
                      />
                    )
                  )}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className={styles.logoSection}>
          {isCollapsed ? (
            <button
              className={styles.logoBtnSmall}
              onClick={() => navigate(routeWithPermission)}
            >
              <img src={KollabHRBadgeLogo} alt="KollabHR" />
            </button>
          ) : (
            <button
              className={cn(styles.logoBtn, styles.logoBtnReveal)}
              onClick={() => navigate(routeWithPermission)}
            >
              <img src={KollabHRBadgeFullLogo} alt="KollabHR" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
